import { useEffect, useState } from 'react';

import { CheckCircle as CheckCircleIcon, Error as ErrorIcon } from '@mui/icons-material';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSearchParams } from 'react-router';

import { logger } from '@/utils/logger';

interface OAuthRedirectProps {
  provider: string;
  /** API endpoint for auth code exchange: 'sfAgent' for Salesforce, 'transformer' for Blackboard */
  apiEndpoint?: 'sfAgent' | 'transformer';
  /** URL path for auth code exchange: 'authCode' for Salesforce, 'bsAuthCode' for Blackboard */
  authCodePath?: string;
}

/**
 * OAuth redirect handler for popup windows.
 *
 * Due to Chrome's storage partitioning, popups that navigate through third-party
 * domains (like Salesforce) cannot access localStorage from the opener window.
 * This means the popup cannot retrieve Auth0 tokens to make authenticated API calls.
 *
 * Solution: Send the auth code data to the opener via postMessage, and let the
 * opener (which has access to Auth0 tokens) make the API call.
 */
export const OAuthRedirect = ({ provider, apiEndpoint, authCodePath }: OAuthRedirectProps) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Completing authorization...');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      logger.error('[OAuth] Error in redirect:', error);
      setStatus('error');
      setMessage('Authorization was denied or failed.');
      window.opener?.postMessage({ lingkOauthMessage: 'failed' }, '*');
      setTimeout(() => window.close(), 2000);
      return;
    }

    if (!code || !state) {
      logger.error('[OAuth] Missing code or state:', { code: !!code, state: !!state });
      setStatus('error');
      setMessage('Invalid authorization response.');
      window.opener?.postMessage({ lingkOauthMessage: 'failed' }, '*');
      setTimeout(() => window.close(), 2000);
      return;
    }

    const stateArray = state.split(state.includes('+') ? '+' : ' ');
    const authCodeRequestGuid = stateArray[0];
    const environmentId = stateArray[2] ? parseInt(stateArray[2], 10) : undefined;

    // Send auth code data to opener for processing (opener has Auth0 tokens and PKCE verifier)
    if (apiEndpoint && authCodePath) {
      window.opener?.postMessage(
        {
          lingkOauthMessage: 'authCode',
          authCode: code,
          authCodeRequestGuid,
          oauthState: state,
          environmentId: environmentId && !isNaN(environmentId) ? environmentId : undefined,
          apiEndpoint,
          authCodePath,
        },
        '*'
      );
      setStatus('success');
      setMessage('Authorization complete! You can close this window.');
      setTimeout(() => window.close(), 1000);
    } else {
      setStatus('success');
      setMessage('Authorization complete! You can close this window.');
      window.opener?.postMessage({ lingkOauthMessage: 'complete' }, '*');
      setTimeout(() => window.close(), 1000);
    }
  }, [searchParams, apiEndpoint, authCodePath]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 4,
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            {provider} Authorization
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Success!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Authorization Failed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {message}
          </Typography>
        </>
      )}
    </Box>
  );
};
